import { useState } from "react";
import { Sparkles, ShieldAlert, Plus, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchTermSet } from "@/types/search-term";
import {
  useAiReviewSearchTerms,
  AiReviewFlaggedItem,
} from "@/resources/search-term/search-term.api";
import {
  useAddProhibitedKeyword,
  useBulkAddProhibitedKeywords,
} from "@/resources/prohibited-keyword/prohibited-keyword.api";
import { toast } from "@/hooks/use-toast";
import type { AiValidationResult } from "@/types/ai-validation";
import { ValidationPanel } from "@/components/ui/ValidationPanel";

interface AiReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termSet: SearchTermSet;
  productName: string;
  brandId: number | null;
}

interface FlaggedKeyword extends AiReviewFlaggedItem {
  addedToProhibited: boolean;
}

export function AiReviewModal({
  open,
  onOpenChange,
  termSet,
  productName,
  brandId,
}: AiReviewModalProps) {
  const [hasScanned, setHasScanned] = useState(false);
  const [flagged, setFlagged] = useState<FlaggedKeyword[]>([]);
  const [reviewValidation, setReviewValidation] = useState<AiValidationResult | null>(null);

  const reviewMutation = useAiReviewSearchTerms();
  const addProhibitedMutation = useAddProhibitedKeyword();
  const bulkAddProhibitedMutation = useBulkAddProhibitedKeywords();

  const handleScan = () => {
    const allWords = termSet.entries
      .flatMap((e) => e.content.split(/\s+/))
      .filter(Boolean);

    const uniqueWords = [...new Set(allWords.map((w) => w.toLowerCase()))];

    reviewMutation.mutate(
      { words: uniqueWords, product_name: productName, product_id: termSet.product_id },
      {
        onSuccess: (result) => {
          setFlagged(
            (result.flagged ?? []).map((f) => ({ ...f, addedToProhibited: false })),
          );
          if (result.validation) {
            setReviewValidation(result.validation);
          }
          setHasScanned(true);
        },
      },
    );
  };

  const handleAddToProhibited = (keyword: string) => {
    if (!brandId) return;

    setFlagged((prev) =>
      prev.map((f) =>
        f.keyword === keyword ? { ...f, addedToProhibited: true } : f,
      ),
    );

    addProhibitedMutation.mutate(
      {
        brand_id: brandId,
        keyword,
        origin: "ai_review",
      },
      {
        onSuccess: (result) => {
          if (result.removed_from_search_terms?.modified_entries) {
            toast({
              title: "Search Terms Updated",
              description: `Removed "${keyword}" from ${result.removed_from_search_terms.modified_entries} search term entries.`,
            });
          }
        },
      },
    );
  };

  const handleAddAllToProhibited = () => {
    if (!brandId) return;

    const notYetAdded = flagged.filter((f) => !f.addedToProhibited);
    if (notYetAdded.length === 0) return;

    setFlagged((prev) => prev.map((f) => ({ ...f, addedToProhibited: true })));

    bulkAddProhibitedMutation.mutate(
      {
        brand_id: brandId,
        keywords: notYetAdded.map((f) => f.keyword),
        origin: "ai_review",
      },
      {
        onSuccess: (result) => {
          if (result.removed_from_search_terms?.modified_entries) {
            toast({
              title: "Search Terms Updated",
              description: `Removed prohibited keywords from ${result.removed_from_search_terms.modified_entries} search term entries.`,
            });
          }
        },
      },
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setHasScanned(false);
      setFlagged([]);
      setReviewValidation(null);
    }
    onOpenChange(nextOpen);
  };

  const severityColor = (severity: string) => {
    switch (severity) {
      case "high":
        return "destructive";
      case "medium":
        return "default";
      case "low":
        return "secondary";
      default:
        return "secondary";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Review — Prohibited Keywords Check
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          AI will scan these search terms for potentially prohibited keywords, competitor brand names, or policy-violating terms that may not be in your prohibited list.
        </p>

        {!hasScanned ? (
          <div className="flex flex-col items-center py-8 gap-4">
            <ShieldAlert className="h-12 w-12 text-muted-foreground/40" />
            <Button onClick={handleScan} disabled={reviewMutation.isPending}>
              {reviewMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Run AI Review
                </>
              )}
            </Button>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3 pr-3">
              {flagged.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No flagged keywords.
                </div>
              ) : (
                flagged.map((item) => (
                  <div
                    key={item.keyword}
                    className="rounded-lg border border-border p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium font-mono text-sm">
                          {item.keyword}
                        </span>
                        <Badge variant={severityColor(item.severity)} className="text-xs">
                          {item.severity}
                        </Badge>
                      </div>
                      {item.addedToProhibited ? (
                        <Badge variant="outline" className="text-xs gap-1">
                          <Check className="h-3 w-3" />
                          Added
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-on-white"
                          className="text-xs h-7"
                          onClick={() => handleAddToProhibited(item.keyword)}
                          disabled={addProhibitedMutation.isPending || bulkAddProhibitedMutation.isPending}
                        >
                          {addProhibitedMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                          Add to Prohibited
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{item.reason}</p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        )}

        {hasScanned && flagged.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {flagged.filter((f) => f.addedToProhibited).length} of {flagged.length} flagged keyword
              {flagged.length !== 1 ? "s" : ""} added to prohibited list
            </span>
            {flagged.some((f) => !f.addedToProhibited) && (
              <Button
                size="sm"
                variant="outline-on-white"
                className="text-xs h-7"
                onClick={handleAddAllToProhibited}
                disabled={bulkAddProhibitedMutation.isPending || addProhibitedMutation.isPending}
              >
                {bulkAddProhibitedMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="h-3 w-3" />
                )}
                Add All to Prohibited
              </Button>
            )}
          </div>
        )}

        {hasScanned && <ValidationPanel validation={reviewValidation} className="mt-2" />}
      </DialogContent>
    </Dialog>
  );
}
